/**
 * edgeGeometry — pixel-faithful port of the Rappid link geometry used by the old
 * EDG (phase 6b, step 2).
 *
 * The old EDG drew every dependency / inheritance with a JointJS `dia.Link`
 * configured (see `lib/joint/factories.js:createEDGLink` and
 * `lib/joint/index.js:createPaper`) as:
 *   - default anchor      : `{ name: 'center' }`         (node bbox centre)
 *   - connection point    : `{ name: 'boundary', args: { sticky: true, stroke: true } }`
 *   - connector           : `{ name: 'rounded' }`        (default radius 10)
 *   - route               : the link's persisted vertices (no router)
 *
 * This module reimplements ONLY the maths JointJS ran to turn
 * (source rect, target rect, vertices) into the final SVG `d` string — copied
 * line-for-line from `rappid/dist/joint.core.js`:
 *   - `joint.connectors.rounded`            (joint.core.js:22830)
 *   - `boundaryIntersection` connection pt  (joint.core.js:24103) for a Rect shape
 *   - the `offset` / `stroke` helpers        (joint.core.js:24033)
 *   - `g.Rect.intersectionWithLine`          (joint.core.js:4152)
 *   - `g.Rect.pointNearestToPoint`           (sticky fallback, joint.core.js:4324)
 *   - `g.Line` segment-segment intersection  (joint.core.js:1827)
 *   - decimalsRounding 2 on anchors + connection points (joint.core.js:15100)
 *
 * No JointJS / Rappid import: this is a standalone numeric port so the new code
 * carries zero jQuery/Backbone/Rappid dependency. Everything is render-only.
 */

export type Pt = { x: number; y: number };
export type Rect = { x: number; y: number; width: number; height: number };

// JointJS rounds anchors + connection points to 2 decimals (paper.decimalsRounding).
const DECIMALS = 2;
// `joint.connectors.rounded` default corner radius (joint.core.js:22834).
const ROUNDED_RADIUS = 10;

function round2(n: number): number {
  const f = Math.pow(10, DECIMALS);
  return Math.round(n * f) / f;
}

function roundPt(p: Pt): Pt {
  return { x: round2(p.x), y: round2(p.y) };
}

function distance(a: Pt, b: Pt): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * `g.Point.move(ref, length)` (joint.core.js:1542) — move `p` a `length` AWAY
 * from `ref` (positive length => away from ref; negative length => towards ref).
 *
 * This matches JointJS's sign convention verbatim so every call site below reads
 * 1:1 against `joint.core.js`. JointJS computes the destination by walking
 * `length` along the ray *from `ref` through `p`*, i.e. `p + (p - ref) * length/d`.
 */
function move(p: Pt, ref: Pt, length: number): Pt {
  const d = distance(p, ref);
  if (d === 0) return { x: p.x, y: p.y };
  const t = length / d;
  return { x: p.x + (p.x - ref.x) * t, y: p.y + (p.y - ref.y) * t };
}

function rectCenter(r: Rect): Pt {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

/**
 * `g.Line(start,end).intersect(g.Line(a,b))` — bounded segment/segment
 * intersection (joint.core.js:1827). Returns the point or null.
 */
function segmentIntersect(s1: Pt, e1: Pt, s2: Pt, e2: Pt): Pt | null {
  const d1x = e1.x - s1.x;
  const d1y = e1.y - s1.y;
  const d2x = e2.x - s2.x;
  const d2y = e2.y - s2.y;
  const det = d1x * d2y - d1y * d2x;
  const dpx = s2.x - s1.x;
  const dpy = s2.y - s1.y;
  const alpha = dpx * d2y - dpy * d2x;
  const beta = dpx * d1y - dpy * d1x;

  if (det === 0 || alpha * det < 0 || beta * det < 0) return null;
  if (det > 0) {
    if (alpha > det || beta > det) return null;
  } else {
    if (alpha < det || beta < det) return null;
  }
  return { x: s1.x + (alpha * d1x) / det, y: s1.y + (alpha * d1y) / det };
}

function rectLines(r: Rect): [Pt, Pt][] {
  const tl = { x: r.x, y: r.y };
  const tr = { x: r.x + r.width, y: r.y };
  const br = { x: r.x + r.width, y: r.y + r.height };
  const bl = { x: r.x, y: r.y + r.height };
  // top, right, bottom, left — same order JointJS uses.
  return [
    [tl, tr],
    [tr, br],
    [br, bl],
    [bl, tl],
  ];
}

/** `g.Rect.intersectionWithLine` (joint.core.js:4152) — all boundary hits or null. */
function rectIntersectLine(r: Rect, ls: Pt, le: Pt): Pt[] | null {
  const out: Pt[] = [];
  const seen = new Set<string>();
  for (const [a, b] of rectLines(r)) {
    const pt = segmentIntersect(ls, le, a, b);
    if (pt) {
      const key = `${pt.x}@${pt.y}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(pt);
      }
    }
  }
  return out.length > 0 ? out : null;
}

function containsPoint(r: Rect, p: Pt): boolean {
  return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height;
}

/** `g.Point.adhereToRect` — clamp p inside r. */
function adhereToRect(r: Rect, p: Pt): Pt {
  return {
    x: Math.min(Math.max(p.x, r.x), r.x + r.width),
    y: Math.min(Math.max(p.y, r.y), r.y + r.height),
  };
}

/** `g.Rect.sideNearestToPoint` (joint.core.js:4372). */
function sideNearestToPoint(r: Rect, p: Pt): 'left' | 'right' | 'top' | 'bottom' {
  const distToLeft = p.x - r.x;
  const distToRight = r.x + r.width - p.x;
  const distToTop = p.y - r.y;
  const distToBottom = r.y + r.height - p.y;
  let closest = distToLeft;
  let side: 'left' | 'right' | 'top' | 'bottom' = 'left';
  if (distToRight < closest) {
    closest = distToRight;
    side = 'right';
  }
  if (distToTop < closest) {
    closest = distToTop;
    side = 'top';
  }
  if (distToBottom < closest) {
    side = 'bottom';
  }
  return side;
}

/** `g.Rect.pointNearestToPoint` (joint.core.js:4324). */
function pointNearestToPoint(r: Rect, p: Pt): Pt {
  if (containsPoint(r, p)) {
    switch (sideNearestToPoint(r, p)) {
      case 'right':
        return { x: r.x + r.width, y: p.y };
      case 'left':
        return { x: r.x, y: p.y };
      case 'bottom':
        return { x: p.x, y: r.y + r.height };
      case 'top':
        return { x: p.x, y: r.y };
    }
  }
  return adhereToRect(r, p);
}

/** Closest of several intersection points to `ref`. */
function closest(points: Pt[], ref: Pt): Pt {
  let best = points[0];
  let bestD = distance(best, ref);
  for (let i = 1; i < points.length; i++) {
    const d = distance(points[i], ref);
    if (d < bestD) {
      best = points[i];
      bestD = d;
    }
  }
  return best;
}

/**
 * Boundary connection point for a RECT node, matching
 * `boundaryIntersection(...)` with args `{ sticky: true, stroke: true }`
 * (joint.core.js:24103) and the object-type rect (stroke-width 2 => offset 1px).
 *
 * @param rect       the node rect (uimodel position + size).
 * @param refPoint   `line.start` — the adjacent route point (first/last vertex,
 *                   or the opposite anchor when there are no vertices).
 * @param anchor     `line.end` — this node's anchor (centre).
 * @param strokeWidth the rect stroke-width (object-type = 2).
 */
function boundaryConnectionPoint(rect: Rect, refPoint: Pt, anchor: Pt, strokeWidth: number): Pt {
  // localShape == the rect; localRef == line.start == refPoint, localLine == line.
  let intersection: Pt | null = null;
  const hits = rectIntersectLine(rect, refPoint, anchor);
  if (hits) {
    intersection = hits.length > 1 ? closest(hits, refPoint) : hits[0];
  } else {
    // sticky: no intersection => nearest boundary point to the reference.
    intersection = pointNearestToPoint(rect, refPoint);
  }
  const cp = intersection ?? anchor;
  // opt.offset (0) + stroke(node)/2 ; stroke => pull the point inward (towards the
  // reference / opposite node) by 1px so the rect's 2px stroke sits centred on the
  // connection point.
  const cpOffset = strokeWidth / 2;
  // `offset(cp, line.start, cpOffset)` (joint.core.js:24033) == JointJS
  // `cp.move(line.start, -Math.min(cpOffset, length - 1))`: move cp TOWARDS
  // refPoint by min(cpOffset, distance - 1).
  const d = distance(cp, refPoint);
  if (cpOffset === 0 && d > 0) return cp;
  return move(cp, refPoint, -Math.min(cpOffset, d - 1));
}

/**
 * `joint.connectors.rounded` (joint.core.js:22830). Produces an SVG path `d`
 * string from sourcePoint, targetPoint and the in-between route (vertices),
 * rounding each route corner with radius 10.
 */
export function roundedConnector(sourcePoint: Pt, targetPoint: Pt, route: Pt[]): string {
  const offset = ROUNDED_RADIUS;
  const _13 = 1 / 3;
  const _23 = 2 / 3;

  const parts: string[] = [];
  parts.push(`M ${fmt(sourcePoint.x)} ${fmt(sourcePoint.y)}`);

  let nextDistance: number | undefined;

  for (let index = 0; index < route.length; index++) {
    const curr = route[index];
    const prev = route[index - 1] || sourcePoint;
    const next = route[index + 1] || targetPoint;

    const prevDistance = nextDistance ?? distance(curr, prev) / 2;
    nextDistance = distance(curr, next) / 2;

    // JointJS: `curr.clone().move(prev, -Math.min(offset, prevDistance))` — the
    // negative length pulls the corner anchor TOWARDS the neighbour (prev/next),
    // landing it BETWEEN the vertex and its neighbour.
    const startMove = -Math.min(offset, prevDistance);
    const endMove = -Math.min(offset, nextDistance);

    const roundedStart = roundInt(move(curr, prev, startMove));
    const roundedEnd = roundInt(move(curr, next, endMove));

    const control1 = {
      x: _13 * roundedStart.x + _23 * curr.x,
      y: _23 * curr.y + _13 * roundedStart.y,
    };
    const control2 = {
      x: _13 * roundedEnd.x + _23 * curr.x,
      y: _23 * curr.y + _13 * roundedEnd.y,
    };

    parts.push(`L ${fmt(roundedStart.x)} ${fmt(roundedStart.y)}`);
    parts.push(
      `C ${fmt(control1.x)} ${fmt(control1.y)} ${fmt(control2.x)} ${fmt(control2.y)} ` +
        `${fmt(roundedEnd.x)} ${fmt(roundedEnd.y)}`
    );
  }

  parts.push(`L ${fmt(targetPoint.x)} ${fmt(targetPoint.y)}`);
  return parts.join(' ');
}

// `g.Point.round()` (no precision) => integer, used inside the rounded connector.
function roundInt(p: Pt): Pt {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}

// Match JointJS path serialization: trim trailing zeros, keep it compact.
function fmt(n: number): string {
  return `${+n.toFixed(3)}`;
}

// ---------------------------------------------------------------------------
// Ellipse boundary geometry (phase 6d) — ported verbatim from JointJS for the
// FSM nodes, whose shapes are ELLIPSES (and circles, a special ellipse with
// a === b). The old FSM paper used the SAME `boundary` connection point with
// `{ sticky: true, stroke: true }` (lib/joint/factories.js:createPaper), so the
// only difference from the EDG's rect anchoring is the shape intersection: a
// `g.Ellipse` instead of a `g.Rect`.
//
// Sources (rappid/dist/joint.core.js, JointJS 2.2.1):
//   - `g.Ellipse.intersectionWithLine`                 (segment/ellipse hits)
//   - `g.Ellipse.intersectionWithLineFromCenterToPoint` (sticky fallback)
//   - the same `offset(cp, line.start, stroke/2)` move as the rect path.
// ---------------------------------------------------------------------------

/** An ellipse node shape: centre (x,y) + semi-axes a (rx) and b (ry). */
export type EllipseShape = { x: number; y: number; a: number; b: number };

/** The ellipse centred in a node rect (semi-axes = half the bbox). */
export function ellipseForRect(r: Rect): EllipseShape {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2, a: r.width / 2, b: r.height / 2 };
}

/** `g.Point.lerp(p, t)` — linear interpolation from `a` to `b` by ratio t. */
function lerp(a: Pt, b: Pt, t: number): Pt {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * `g.Ellipse.intersectionWithLine(line)` (joint.core.js) — all hits of the
 * bounded segment `start→end` with the ellipse boundary, or null. The maths is
 * copied line-for-line from JointJS (solving the quadratic in the line
 * parameter), so the FSM endpoints land on the same pixels Rappid produced.
 */
function ellipseIntersectLine(e: EllipseShape, start: Pt, end: Pt): Pt[] | null {
  const out: Pt[] = [];
  const a = e.a;
  const b = e.b;
  const v = { x: end.x - start.x, y: end.y - start.y }; // line.vector()
  const h = { x: start.x - e.x, y: start.y - e.y }; // start.difference(center)
  const u = { x: v.x / (a * a), y: v.y / (b * b) };
  const aPt = { x: h.x / (a * a), y: h.y / (b * b) };
  const c = v.x * u.x + v.y * u.y; // o.dot(u)
  const l = v.x * aPt.x + v.y * aPt.y; // o.dot(a)
  const f = l * l - c * (h.x * aPt.x + h.y * aPt.y - 1);

  if (f < 0) return null;
  if (f > 0) {
    const g = Math.sqrt(f);
    const p = (-l - g) / c;
    const d = (-l + g) / c;
    if ((p < 0 || p > 1) && (d < 0 || d > 1)) return null;
    if (p >= 0 && p <= 1) out.push(lerp(start, end, p));
    if (d >= 0 && d <= 1) out.push(lerp(start, end, d));
  } else {
    const vv = -l / c;
    if (!(vv >= 0 && vv <= 1)) return null;
    out.push(lerp(start, end, vv));
  }
  return out.length > 0 ? out : null;
}

/**
 * `g.Ellipse.intersectionWithLineFromCenterToPoint(point)` (joint.core.js) — the
 * sticky fallback when the link line misses the ellipse boundary. Returns the
 * boundary point on the ray from the ellipse centre through `point` (no rotation,
 * FSM ellipses are axis-aligned). Verbatim port.
 */
function ellipseIntersectFromCenter(e: EllipseShape, point: Pt): Pt {
  const i = point.x - e.x;
  const r = point.y - e.y;
  if (i === 0) {
    // Vertical line through the centre: nearest bbox point (top/bottom vertex).
    return { x: e.x, y: r < 0 ? e.y - e.b : e.y + e.b };
  }
  const s = r / i;
  const o = s * s;
  const hh = e.a * e.a;
  const u = e.b * e.b;
  let aa = Math.sqrt(1 / (1 / hh + o / u));
  aa = i < 0 ? -aa : aa;
  const cc = s * aa;
  return { x: e.x + aa, y: e.y + cc };
}

/**
 * Boundary connection point for an ELLIPSE node, matching JointJS
 * `connectionPoints.boundary` with `{ sticky: true, stroke: true }`
 * (joint.core.js) for an `Ellipse` shape. Mirrors `boundaryConnectionPoint`
 * (the rect version) exactly, only swapping the shape intersection:
 *   - intersect the link line with the ellipse; pick the hit closest to the ref,
 *   - sticky fallback = centre→ref ray boundary point,
 *   - pull the point inward by `stroke/2` (1px circle/0.5px ellipse) towards ref.
 */
function ellipseConnectionPoint(
  ellipse: EllipseShape,
  refPoint: Pt,
  anchor: Pt,
  strokeWidth: number
): Pt {
  let intersection: Pt | null = null;
  const hits = ellipseIntersectLine(ellipse, refPoint, anchor);
  if (hits) {
    intersection = hits.length > 1 ? closest(hits, refPoint) : hits[0];
  } else {
    intersection = ellipseIntersectFromCenter(ellipse, refPoint);
  }
  const cp = intersection ?? anchor;
  const cpOffset = strokeWidth / 2;
  const d = distance(cp, refPoint);
  if (cpOffset === 0 && d > 0) return cp;
  return move(cp, refPoint, -Math.min(cpOffset, d - 1));
}

export type EdgePath = {
  /** The full SVG path `d` for the connection. */
  d: string;
  /** The boundary connection point at the source node. */
  source: Pt;
  /** The boundary connection point at the target node. */
  target: Pt;
  /** Source point + vertices + target point — the full polyline of waypoints. */
  points: Pt[];
};

export type ComputeEdgePathInput = {
  sourceRect: Rect;
  targetRect: Rect;
  /** Persisted vertices from the uimodel link (already includes collision math). */
  vertices: Pt[];
  /** Source rect stroke-width (object-type = 2). */
  sourceStroke: number;
  /** Target rect stroke-width (object-type = 2). */
  targetStroke: number;
};

/**
 * Full geometry pipeline matching JointJS `LinkView.findConnectionPoints` +
 * `connectors.rounded`:
 *   1. anchors = node centres (rounded to 2 dp).
 *   2. route   = vertices.
 *   3. source connection point: boundary( line.start = firstVertex || targetAnchor,
 *      line.end = sourceAnchor ), rounded to 2 dp.
 *   4. target connection point: boundary( line.start = lastVertex || sourceAnchor,
 *      line.end = targetAnchor ), rounded to 2 dp.
 *   5. rounded connector through [sourcePoint, ...vertices, targetPoint].
 */
export function computeEdgePath(input: ComputeEdgePathInput): EdgePath {
  const { sourceRect, targetRect, vertices, sourceStroke, targetStroke } = input;

  // 1. anchors (center), rounded to 2 decimals like getAnchor.
  const sourceAnchor = roundPt(rectCenter(sourceRect));
  const targetAnchor = roundPt(rectCenter(targetRect));

  const firstWaypoint = vertices[0];
  const lastWaypoint = vertices[vertices.length - 1];

  // 3. source connection point.
  const sourceRef = firstWaypoint ?? targetAnchor;
  const sourcePoint = roundPt(
    boundaryConnectionPoint(sourceRect, sourceRef, sourceAnchor, sourceStroke)
  );

  // 4. target connection point.
  const targetRef = lastWaypoint ?? sourceAnchor;
  const targetPoint = roundPt(
    boundaryConnectionPoint(targetRect, targetRef, targetAnchor, targetStroke)
  );

  // 5. rounded connector path.
  const d = roundedConnector(sourcePoint, targetPoint, vertices);

  return {
    d,
    source: sourcePoint,
    target: targetPoint,
    points: [sourcePoint, ...vertices, targetPoint],
  };
}

export type ComputeEllipseEdgePathInput = {
  /** Source node rect (uimodel position + size); the ellipse fills it. */
  sourceRect: Rect;
  /** Target node rect. */
  targetRect: Rect;
  /** Persisted vertices (self-loops always carry >= 2, fixed in fsmlink.js). */
  vertices: Pt[];
  /** Source ellipse stroke-width (FSM state = 1; initial circle = 2). */
  sourceStroke: number;
  /** Target ellipse stroke-width. */
  targetStroke: number;
};

/**
 * Full geometry pipeline for an FSM transition, identical to `computeEdgePath`
 * but anchoring on ELLIPSE node boundaries (FSM states are ellipses; the initial
 * state is a circle === ellipse with a === b). The old FSM paper used the same
 * `boundary` connection point + `rounded` connector through the persisted
 * vertices, so a self-loop (>= 2 vertices) draws the same arc out of and back
 * into the node, anchored on the ellipse boundary on both ends.
 */
export function computeEllipseEdgePath(input: ComputeEllipseEdgePathInput): EdgePath {
  const { sourceRect, targetRect, vertices, sourceStroke, targetStroke } = input;

  const sourceEllipse = ellipseForRect(sourceRect);
  const targetEllipse = ellipseForRect(targetRect);

  // 1. anchors (ellipse centres === rect centres), rounded to 2 dp like getAnchor.
  const sourceAnchor = roundPt(rectCenter(sourceRect));
  const targetAnchor = roundPt(rectCenter(targetRect));

  const firstWaypoint = vertices[0];
  const lastWaypoint = vertices[vertices.length - 1];

  // 2. source connection point: line from (first vertex || target anchor) to the
  //    source anchor, intersected with the source ellipse.
  const sourceRef = firstWaypoint ?? targetAnchor;
  const sourcePoint = roundPt(
    ellipseConnectionPoint(sourceEllipse, sourceRef, sourceAnchor, sourceStroke)
  );

  // 3. target connection point.
  const targetRef = lastWaypoint ?? sourceAnchor;
  const targetPoint = roundPt(
    ellipseConnectionPoint(targetEllipse, targetRef, targetAnchor, targetStroke)
  );

  // 4. rounded connector through the boundary points + vertices.
  const d = roundedConnector(sourcePoint, targetPoint, vertices);

  return {
    d,
    source: sourcePoint,
    target: targetPoint,
    points: [sourcePoint, ...vertices, targetPoint],
  };
}

/**
 * Point + tangent angle (deg) at a given arc-length distance along a polyline.
 * Used to place edge labels exactly the way JointJS placed them along the
 * connection path:
 *   - a positive number is an absolute distance from the START,
 *   - a negative number is an absolute distance from the END,
 *   - a `'NN%'` string is a ratio of the total length.
 * The label sits centred on that point (default JointJS label offset 0).
 *
 * Note: JointJS measures distance along the actual (rounded) connection path; the
 * rounded corners only shave a few px near vertices, so measuring along the
 * straight polyline of waypoints is within sub-pixel of the curved arc-length for
 * label placement and matches the visible label position.
 */
export function pointAtLength(points: Pt[], position: number | string): Pt {
  const segLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const len = distance(points[i], points[i + 1]);
    segLengths.push(len);
    total += len;
  }
  if (total === 0) return { x: points[0].x, y: points[0].y };

  let target: number;
  if (typeof position === 'string') {
    const ratio = parseFloat(position) / 100;
    target = ratio * total;
  } else if (position < 0) {
    target = total + position; // distance from the end
  } else {
    target = position;
  }
  target = Math.max(0, Math.min(total, target));

  let acc = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const len = segLengths[i];
    if (acc + len >= target || i === segLengths.length - 1) {
      const t = len === 0 ? 0 : (target - acc) / len;
      const a = points[i];
      const b = points[i + 1];
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    acc += len;
  }
  const last = points[points.length - 1];
  return { x: last.x, y: last.y };
}

/**
 * FSM transition label placement — port of JointJS `LinkView.getLabelCoordinates`
 * (joint.core.js) for the `{ distance, offset }` label position the old FSM used.
 *
 * The old FSM label (lib/joint/fsm.js:setTransitionLabel) is a single label at
 * `{ distance: 0.5 (default), offset: number }`. JointJS, for a NUMBER offset (the
 * FSM case — no x/y offset object), takes the tangent line at the distance point,
 * rotates it -90deg about that point, sets its length to `offset`, and uses the
 * line END as the label centre. That is: label = point + offset * perp(tangent),
 * where rotating the unit tangent (ux, uy) by JointJS angle -90 maps it to
 * (-uy, ux). With a left→right path the perpendicular is (0, +1) — DOWN in SVG —
 * so a positive offset drops the label below the line, matching the baseline.
 *
 * `distance` in (0, 1] is a RATIO of the connection length (i = 0 < e <= 1);
 * any other value is treated as an absolute length (matching JointJS).
 */
export function labelPointWithOffset(points: Pt[], distance: number, offset: number): Pt {
  const segLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const len = distance0(points[i], points[i + 1]);
    segLengths.push(len);
    total += len;
  }
  if (total === 0) return { x: points[0].x, y: points[0].y };

  // JointJS: i = 0 < distance <= 1 => ratio; otherwise absolute length.
  const isRatio = distance > 0 && distance <= 1;
  let target = isRatio ? distance * total : distance;
  target = Math.max(0, Math.min(total, target));

  // Locate the segment containing `target`; the unit tangent is that segment's
  // direction (the polyline tangent — sub-pixel from the rounded path's tangent).
  let acc = 0;
  let point: Pt = { x: points[0].x, y: points[0].y };
  let ux = 1;
  let uy = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const len = segLengths[i];
    if (acc + len >= target || i === segLengths.length - 1) {
      const a = points[i];
      const b = points[i + 1];
      const t = len === 0 ? 0 : (target - acc) / len;
      point = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      if (len > 0) {
        ux = (b.x - a.x) / len;
        uy = (b.y - a.y) / len;
      }
      break;
    }
    acc += len;
  }

  // rotate the unit tangent by JointJS angle -90 => (-uy, ux), then scale by offset.
  return { x: point.x + -uy * offset, y: point.y + ux * offset };
}

// Local alias so labelPointWithOffset reads against the same `distance` helper as
// the rest of the module without shadowing its `distance` parameter name.
function distance0(a: Pt, b: Pt): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/** A persisted FSM label position: a ratio distance + a perpendicular offset. */
export type LabelPosition = { distance: number; offset: number };

/**
 * Inverse of `labelPointWithOffset` — port of JointJS `LinkView.getLabelPosition`
 * (joint.core.js) for the drag-to-reposition of an FSM transition label.
 *
 * When the user drags the label to a point `p` (in FLOW coords), JointJS:
 *   1. finds the closest point on the connection polyline to `p`,
 *   2. records `distance` as the RATIO of that closest point's arc-length to the
 *      total length (the FSM always persisted a ratio in (0, 1]),
 *   3. records `offset` as the SIGNED perpendicular scalar so that
 *      `closestPoint + offset * perp(tangent) === p`, where the perpendicular is
 *      the tangent rotated by -90 → (-uy, ux) (the same rotation
 *      `labelPointWithOffset` uses), so the round-trip is exact.
 *
 * The result feeds the EXISTING `updateFSMLinkLabelPosition` command (offset +
 * distance), so the persisted schema is unchanged and autosave/undo are free.
 */
export function labelPositionFromPoint(points: Pt[], p: Pt): LabelPosition {
  const segLengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const len = distance0(points[i], points[i + 1]);
    segLengths.push(len);
    total += len;
  }
  if (total === 0) return { distance: 0.5, offset: 0 };

  // Find the closest point across all segments + its arc-length and tangent.
  let bestDistSq = Infinity;
  let bestLength = 0; // arc-length of the closest point from the start
  let bestUx = 1;
  let bestUy = 0;
  let bestPoint: Pt = { x: points[0].x, y: points[0].y };
  let acc = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const a = points[i];
    const b = points[i + 1];
    const len = segLengths[i];
    let t = 0;
    if (len > 0) {
      t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / (len * len);
      t = Math.max(0, Math.min(1, t));
    }
    const proj = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const dSq = (p.x - proj.x) ** 2 + (p.y - proj.y) ** 2;
    if (dSq < bestDistSq) {
      bestDistSq = dSq;
      bestLength = acc + len * t;
      bestPoint = proj;
      if (len > 0) {
        bestUx = (b.x - a.x) / len;
        bestUy = (b.y - a.y) / len;
      }
    }
    acc += len;
  }

  // distance as a ratio (matches the FSM's persisted ratio form).
  const ratio = total === 0 ? 0.5 : bestLength / total;
  // perp(tangent) rotated -90 => (-uy, ux); offset = (p - closest) · perp.
  const perpX = -bestUy;
  const perpY = bestUx;
  const offset = (p.x - bestPoint.x) * perpX + (p.y - bestPoint.y) * perpY;

  return { distance: ratio, offset };
}
